package components.project

import components.editorNameForText
import components.userName
import it.neckar.lizergy.model.ElementsSelectionEntry
import it.neckar.lizergy.model.company.CompanyResolver
import it.neckar.lizergy.model.company.UserResolver
import it.neckar.lizergy.model.company.user.AccessRights
import it.neckar.lizergy.model.company.user.UserInformation
import it.neckar.lizergy.model.configuration.components.Einspeiseart
import it.neckar.lizergy.model.configuration.components.formatDescription
import it.neckar.lizergy.model.configuration.quote.builder.BasicBatteryInverter
import it.neckar.lizergy.model.configuration.quote.builder.HybridInverter
import it.neckar.lizergy.model.project.previews.ProjectQueryComponent
import it.neckar.lizergy.model.project.previews.ServerProjectPreview
import it.neckar.lizergy.model.project.process.state.AdvanceInvoiceProcessStateEntry.AdvanceInvoiceProcessStates
import it.neckar.lizergy.model.project.process.state.AssemblyBasementPreparationProcessStateEntry.AssemblyBasementPreparationProcessStates
import it.neckar.lizergy.model.project.process.state.AssemblyBasementProcessStateEntry.AssemblyBasementProcessStates
import it.neckar.lizergy.model.project.process.state.AssemblyPortfolioProcessStateEntry.AssemblyPortfolioProcessStates
import it.neckar.lizergy.model.project.process.state.AssemblyRoofPreparationProcessStateEntry.AssemblyRoofPreparationProcessStates
import it.neckar.lizergy.model.project.process.state.AssemblyRoofProcessStateEntry.AssemblyRoofProcessStates
import it.neckar.lizergy.model.project.process.state.BlueprintAcquisitionProcessStateEntry.BlueprintAcquisitionProcessStates
import it.neckar.lizergy.model.project.process.state.BlueprintProcessStateEntry.BlueprintProcessStates
import it.neckar.lizergy.model.project.process.state.ConfigurationProcessStateEntry.ConfigurationProcessStates
import it.neckar.lizergy.model.project.process.state.DocumentationProcessStateEntry.DocumentationProcessStates
import it.neckar.lizergy.model.project.process.state.FinalAccountProcessStateEntry.FinalAccountProcessStates
import it.neckar.lizergy.model.project.process.state.FinishingUpProcessStateEntry.FinishingUpProcessStates
import it.neckar.lizergy.model.project.process.state.GridAssessmentProcessStateEntry.GridAssessmentProcessStates
import it.neckar.lizergy.model.project.process.state.LizergyProcessStates
import it.neckar.lizergy.model.project.process.state.OrderSpecialMaterialProcessStateEntry.OrderSpecialMaterialProcessStates
import it.neckar.lizergy.model.project.process.state.PresentationProcessStateEntry.PresentationProcessStates
import it.neckar.lizergy.model.project.process.state.ProjectProcessStateEntry.ProjectProcessStates
import it.neckar.lizergy.model.project.process.state.QuoteConfirmationProcessStateEntry.QuoteConfirmationProcessStates
import it.neckar.lizergy.model.project.process.state.QuoteOfferProcessStateEntry.QuoteOfferProcessStates
import it.neckar.lizergy.model.project.process.state.StartupOperationsProcessStateEntry.StartupOperationsProcessStates
import it.neckar.lizergy.model.project.process.state.SwitchMeterBoxProcessStateEntry.SwitchMeterBoxProcessStates
import it.neckar.open.collections.fastForEach
import it.neckar.open.formatting.dateFormat
import it.neckar.open.formatting.dateTimeFormat
import it.neckar.processStates.ProcessStatePhase
import it.neckar.react.common.*
import it.neckar.react.common.FontAwesome.faCircleCheck
import it.neckar.react.common.button.*
import it.neckar.react.common.router.*
import it.neckar.react.common.table.*
import kotlinx.css.*
import kotlinx.html.TD
import kotlinx.html.classes
import kotlinx.html.title
import plannerI18nConfiguration
import react.dom.*
import router.RouterUrls
import router.toBlueprint
import router.toProject
import services.http.ProjectQuerySortedColumn
import styled.*

object ServerProjectPreviewsTable {

  enum class TableColumnId(val id: String) {
    EditButton("editButton"),
    Project("project"),
    Customer("customer"),
    Maintainer("technicalData"),
    ComponentDetails("componentDetails"),
    Editor("editor"),
    ProcessState("processState"),
    ProcessStates("processStates"),
    ProcessStatesDates("processStatesDates"),
  }

  fun parseTableColumnId(id: String): ProjectQuerySortedColumn {
    val tableColumnId = TableColumnId.entries.firstOrNull { it.id == id } ?: throw IllegalArgumentException("Unknown table column id: $id")
    return when (tableColumnId) {
      TableColumnId.EditButton -> throw IllegalStateException("EditButton is not a valid column for sorting")
      TableColumnId.Project -> ProjectQuerySortedColumn.ProjectColumn
      TableColumnId.Customer -> ProjectQuerySortedColumn.CustomerColumn
      TableColumnId.ComponentDetails -> ProjectQuerySortedColumn.ComponentDetails
      TableColumnId.Maintainer -> ProjectQuerySortedColumn.Maintainer
      TableColumnId.Editor -> ProjectQuerySortedColumn.EditorColumn
      TableColumnId.ProcessState -> ProjectQuerySortedColumn.ProcessStateColumn
      TableColumnId.ProcessStates -> ProjectQuerySortedColumn.ProcessStatesColumn
      TableColumnId.ProcessStatesDates -> ProjectQuerySortedColumn.ProcessStatesDatesColumn
    }
  }

  fun <T : ServerProjectPreview> editButtonFirstColumn(loggedInUser: UserInformation, editAction: (projectPreview: T) -> Unit): TableHeaderColumn<T> {
    return TableHeaderColumn(
      id = TableColumnId.EditButton.id,
    ) { projectPreview ->
      {
        attrs {
          addClass("mt-3")
        }
        editButton {
          editAction(projectPreview)
        }

        if (loggedInUser.accessRights.canAccess(AccessRights.AccessRight.DeleteProjects)) {
          if (projectPreview.processStateEntry?.processState?.inPhase == ProcessStatePhase.Paused) {
            unArchiveModal(projectPreview)
          } else {
            archiveModal(projectPreview)
          }
        }
      }
    }
  }

  fun <T : ServerProjectPreview> getProjectColumn(companyResolver: CompanyResolver): TableDataColumn<T> {
    return TableDataColumn(
      id = TableColumnId.Project.id,
      title = "Projekt",
      sortFunction = compareBy { it.displayName },
    ) { projectPreview ->
      {
        projectDetails(projectPreview, companyResolver)
        startupDateLine(projectPreview)
      }
    }
  }

  fun <T : ServerProjectPreview> getProjectAndCustomerColumn(companyResolver: CompanyResolver): TableDataColumn<T> {
    return TableDataColumn(
      id = TableColumnId.Project.id,
      title = "Projekt",
      sortFunction = compareBy { it.displayName },
    ) { projectPreview ->
      {
        projectDetails(projectPreview, companyResolver)
        div("mt-2") { customerDetails(projectPreview) }
        startupDateLine(projectPreview)
      }
    }
  }

  fun <T : ServerProjectPreview> getCustomerColumn(): TableDataColumn<T> {
    return TableDataColumn(
      id = TableColumnId.Customer.id,
      title = "Kunde:in",
      sortFunction = compareBy { it.customer.completeName },
    ) { projectPreview ->
      {
        customerDetails(projectPreview)
      }
    }
  }

  fun <T : ServerProjectPreview> getComponentDetailsColumn(): TableDataColumn<T> {
    return TableDataColumn(
      id = TableColumnId.ComponentDetails.id,
      title = "Anlage",
      sortFunction = compareBy { it.currentConfigurationPreview?.modulesReport?.totalPowerRating?.kiloWattPeak },
    ) { projectPreview ->
      val configuration = projectPreview.currentConfigurationPreview
      val modulesReport = configuration?.modulesReport
      val batteryConfiguration = configuration?.batteryConfiguration
      val inverterSelection = when (val batteryInverter = batteryConfiguration?.inverterType) {
        is BasicBatteryInverter, is HybridInverter -> configuration.inverterSelection.entriesNonEmpty + ElementsSelectionEntry(batteryInverter, 1)
        null -> configuration?.inverterSelection?.entriesNonEmpty
      }
      val wallboxSelection = configuration?.wallboxSelection

      {
        p("form-text") { +"Anlagenleistung: ${modulesReport?.totalPowerRating?.formatKiloWattPeak(plannerI18nConfiguration) ?: "-"}" }
        if (batteryConfiguration != null) {
          p("form-text") { +"Speicherkapazität: ${batteryConfiguration.totalStorage.formatKiloWattHour(plannerI18nConfiguration)}" }
        }
        when (configuration?.einspeiseart) {
          Einspeiseart.Ueberschuss -> p("form-text mb-3") { +"Verbrauch: ${configuration.powerUsageScenario.yearlyPowerConsumptionValue.formatKiloWattHour(plannerI18nConfiguration)}" }
          Einspeiseart.Volleinspeisung -> p("form-text mb-3") { b { +"Volleinspeisung" } }
          null -> {}
        }

        if (modulesReport != null) {
          p("form-text") { +"Module: ${modulesReport.format()}" }
        }

        if (batteryConfiguration != null) {
          div("form-text") {
            p {
              +"Batterie: ${batteryConfiguration.formatOnlyBattery()}"
            }

            invalidIf { batteryConfiguration.lifeCycleState.isEndOfLife() }
            if (batteryConfiguration.lifeCycleState.isEndOfLife()) {
              p("text-danger") {
                +"Die ausgewählte Batterie ist nicht mehr verfügbar!"
              }
            }
          }
        }

        if (inverterSelection != null) {
          p("form-text") {
            +"Wechselrichter: ${inverterSelection.formatDescription()}"

            invalidIf { inverterSelection.isEmpty() }
          }
        }

        if (wallboxSelection != null && wallboxSelection.isNotEmpty()) {
          p("form-text") {
            +"E-Auto: ${configuration.wallboxSelection.format()}"
          }
        }
      }
    }
  }

  fun <T : ServerProjectPreview> getMaintainerColumn(): TableDataColumn<T> {
    return TableDataColumn(
      id = TableColumnId.Maintainer.id,
      title = "Ansprechpartner",
      sortFunction = compareBy { it.maintainerInformation.editorName },
    ) { projectPreview ->
      val ansprechpartnerDachInformation = projectPreview.ansprechpartnerDachInformation
      val ansprechpartnerElektroInformation = projectPreview.ansprechpartnerElektroInformation
      val maintainerInformation = projectPreview.maintainerInformation

      {
        p("form-text") {
          attrs {
            title = "Ansprechpartner"
          }
          span("badge rounded-pill bg-dark me-2") { i(FontAwesomeIcons.Eye) {} }
          userName(maintainerInformation)
        }
        if (ansprechpartnerDachInformation != null) {
          p("form-text") {
            attrs {
              title = "Ansprechpartner Dach"
            }
            span("badge rounded-pill bg-info me-2") { i(FontAwesomeIcons.SolarPanel) {} }
            userName(ansprechpartnerDachInformation)
          }
        }
        if (ansprechpartnerElektroInformation != null) {
          p("form-text") {
            attrs {
              title = "Ansprechpartner Elektro"
            }
            span("badge rounded-pill bg-warning me-2") { i(FontAwesomeIcons.Bolt) {} }
            userName(ansprechpartnerElektroInformation)
          }
        }
      }
    }
  }

  fun <T : ServerProjectPreview> getEditorColumn(projectQueryComponent: ProjectQueryComponent?, userResolver: UserResolver): TableDataColumn<T> {
    return TableDataColumn(
      id = TableColumnId.Editor.id,
      title = "Bearbeiter",
      sortFunction = compareBy { projectPreview -> projectPreview.editorForQueryComponent(projectQueryComponent)?.let { userResolver[it].editorName } ?: projectPreview.maintainerInformation.editorName },
    ) { projectPreview ->
      {
        projectPreview.editorForQueryComponent(projectQueryComponent)?.let { userName(userResolver[it]) }
        p("form-text") {
          attrs {
            title = "Ansprechpartner"
          }
          span("badge rounded-pill bg-dark me-2") { i(FontAwesomeIcons.Eye) {} }
          userName(projectPreview.maintainerInformation)
        }
        projectPreview.ansprechpartnerDachInformation?.let {
          p("form-text") {
            attrs {
              title = "Ansprechpartner Dach"
            }
            span("badge rounded-pill bg-info me-2") { i(FontAwesomeIcons.SolarPanel) {} }
            userName(it)
          }
        }
        projectPreview.ansprechpartnerElektroInformation?.let {
          p("form-text") {
            attrs {
              title = "Ansprechpartner Elektro"
            }
            span("badge rounded-pill bg-warning me-2") { i(FontAwesomeIcons.Bolt) {} }
            userName(it)
          }
        }
      }
    }
  }

  fun <T : ServerProjectPreview> getProcessStatesColumn(filteredProcessState: LizergyProcessStates?, userResolver: UserResolver): TableDataColumn<T> {
    return TableDataColumn(
      id = TableColumnId.ProcessStates.id,
      title = "Projektstatus",
      sortFunction = compareBy { projectPreview ->
        if (projectPreview.processStateEntry?.processState?.inPhase == ProcessStatePhase.Paused) {
          projectPreview.processStateEntry
        } else {
          projectPreview.processStateEntryForComponent(null)
        }
      },
    ) { projectPreview ->
      {
        attrs {
          addClass("text-end")
        }
        projectPreview.mostAdvancedProcessStateEntries
          ?.filter {
            it.processState.inPhase.isFinished.not() ||
                it.processState == filteredProcessState ||
                listOf(
                  PresentationProcessStates.InReview,
                  GridAssessmentProcessStates.Accepted,
                  AdvanceInvoiceProcessStates.Paid,
                  FinalAccountProcessStates.Paid,
                  DocumentationProcessStates.Done,
                ).contains(it.processState)
          }
          ?.sortedByDescending { it.assignedAt }
          ?.fastForEach {
            p {
              attrs {
                title = "Bearbeiter: ${userResolver[it.assignedTo].editorNameForText()}"
              }
              processStatePill(it)
            }
          }
      }
    }
  }

  fun <T : ServerProjectPreview> getProcessStatesDatesColumn(filteredProcessState: LizergyProcessStates?, userResolver: UserResolver): TableDataColumn<T> {
    return TableDataColumn(
      id = TableColumnId.ProcessStatesDates.id,
      title = "Datum",
      sortFunction = compareBy { projectPreview ->
        val relevantEntry = if (projectPreview.processStateEntry?.processState?.inPhase == ProcessStatePhase.Paused) {
          projectPreview.processStateEntry
        } else {
          projectPreview.processStateEntryForComponent(null)
        }
        relevantEntry?.assignedAt
      },
    ) { projectPreview ->
      {
        projectPreview.mostAdvancedProcessStateEntries
          ?.filter {
            it.processState.inPhase.isFinished.not() ||
                it.processState == filteredProcessState ||
                listOf(
                  PresentationProcessStates.InReview,
                  GridAssessmentProcessStates.Accepted,
                  AdvanceInvoiceProcessStates.Paid,
                  FinalAccountProcessStates.Paid,
                  DocumentationProcessStates.Done,
                ).contains(it.processState)
          }
          ?.sortedByDescending { it.assignedAt }
          ?.fastForEach {
            styledP {
              attrs {
                classes = setOf("form-text")
                title = "Zugewießen von: ${userResolver[it.assignedBy].editorNameForText()}"
              }
              css {
                minHeight = 24.px
              }
              +it.processState.formatDate(it.assignedAt, plannerI18nConfiguration)
            }
          }
      }
    }
  }

  fun <T : ServerProjectPreview> getRelevantProcessStateColumn(projectQueryComponent: ProjectQueryComponent?): TableDataColumn<T> {
    return TableDataColumn(
      id = TableColumnId.ProcessState.id,
      title = "Projektstatus",
      sortFunction = compareBy { projectPreview ->
        val relevantEntry = if (projectPreview.processStateEntry?.processState?.inPhase == ProcessStatePhase.Paused) {
          projectPreview.processStateEntry
        } else {
          projectPreview.processStateEntryForComponent(projectQueryComponent = projectQueryComponent)
        }
        relevantEntry?.assignedAt
      },
    ) { projectPreview ->
      {
        val relevantEntry = if (projectPreview.processStateEntry?.processState?.inPhase == ProcessStatePhase.Paused) {
          projectPreview.processStateEntry
        } else {
          projectPreview.processStateEntryForComponent(projectQueryComponent = projectQueryComponent)
        }
        if (relevantEntry != null) {
          processStatePillWithDate(relevantEntry)
        }
      }
    }
  }

  fun <T : ServerProjectPreview> getMostRecentProcessStateColumn(): TableDataColumn<T> {
    return TableDataColumn(
      id = TableColumnId.ProcessState.id,
      title = "Projektstatus",
      sortFunction = compareBy { projectPreview ->
        val relevantEntry = if (projectPreview.processStateEntry?.processState?.inPhase == ProcessStatePhase.Paused) projectPreview.processStateEntry else projectPreview.mostAdvancedProcessStateEntry
        relevantEntry?.assignedAt
      },
    ) { projectPreview ->
      {
        val relevantEntry = if (projectPreview.processStateEntry?.processState?.inPhase == ProcessStatePhase.Paused) projectPreview.processStateEntry else projectPreview.mostAdvancedProcessStateEntry
        if (relevantEntry != null) {
          processStatePillWithDate(relevantEntry)
        }
      }
    }
  }

  fun <T : ServerProjectPreview> buildColumns(
    tableColumns: List<TableColumn<T>>,
    navigate: NavigateUrlFunction,
    projectQueryComponent: ProjectQueryComponent?,
    loggedInUser: UserInformation,
  ): List<TableColumn<T>> {
    return buildColumns(tableColumns = tableColumns, loggedInUser = loggedInUser) { projectPreview -> editProjectAction(navigate, projectPreview, projectQueryComponent) }
  }

  fun <T : ServerProjectPreview> buildColumns(
    tableColumns: List<TableColumn<T>>,
    loggedInUser: UserInformation,
    editAction: (projectPreview: T) -> Unit,
  ): List<TableColumn<T>> {
    return buildList {
      add(editButtonFirstColumn(loggedInUser, editAction))
      addAll(tableColumns)
    }
  }

  fun <T : ServerProjectPreview> homepageColumns(filteredProcessState: LizergyProcessStates?, userResolver: UserResolver, companyResolver: CompanyResolver): List<TableColumn<T>> {
    return buildList {
      add(getProjectAndCustomerColumn(companyResolver))
      add(getComponentDetailsColumn())
      add(getMaintainerColumn())
      add(getProcessStatesColumn(filteredProcessState, userResolver))
      add(getProcessStatesDatesColumn(filteredProcessState, userResolver))
    }
  }

  fun <T : ServerProjectPreview> defaultColumns(projectQueryComponent: ProjectQueryComponent?, userResolver: UserResolver, companyResolver: CompanyResolver): List<TableColumn<T>> {
    return buildList {
      add(getProjectColumn(companyResolver))
      add(getCustomerColumn())
      add(getEditorColumn(projectQueryComponent, userResolver))
      add(getRelevantProcessStateColumn(projectQueryComponent))
    }
  }

  fun <T : ServerProjectPreview> RDOMBuilder<TD>.projectDetails(projectPreview: T, companyResolver: CompanyResolver) {
    b {
      attrs {
        title = projectPreview.projectId.format()
      }
      +projectPreview.displayName
    }

    span("mx-2") {
      validationIcon(projectPreview.worstProblem, projectPreview.formattedProblems)
    }

    val currentConfiguration = projectPreview.currentConfigurationPreview
    currentConfiguration?.earningsDistribution?.montageDach?.company?.let { montageDachCompany ->
      if (montageDachCompany != projectPreview.sellingCompany) {
        span("badge rounded-pill bg-info ms-2") {
          attrs {
            title = "Dachmontage von ${companyResolver[montageDachCompany].name} durchgeführt"
          }
          i(FontAwesomeIcons.SolarPanel) {}
        }
      }
    }
    currentConfiguration?.earningsDistribution?.elektroInstallation?.company?.let { elektroInstallationCompany ->
      if (elektroInstallationCompany != projectPreview.sellingCompany) {
        span("badge rounded-pill bg-warning ms-2") {
          attrs {
            title = "Elektroinstallation Haus von ${companyResolver[elektroInstallationCompany].name} durchgeführt"
          }
          i(FontAwesomeIcons.Bolt) {}
        }
      }
    }
    if (currentConfiguration?.eigenmontage == true) {
      span("badge rounded-pill bg-dark ms-2") {
        attrs {
          title = "Eigenmontage"
        }
        i(FontAwesomeIcons.ScrewDriverWrench) {}
      }
    }
    if (projectPreview.restArbeiten.isNotEmpty) {
      span("badge rounded-pill bg-danger ms-2") {
        attrs {
          title = "Restarbeiten"
        }
        i(FontAwesomeIcons.PersonDigging) {}
      }
    }
    val verification = projectPreview.verification
    if (verification != null) {
      span("badge rounded-pill bg-success ms-2") {
        attrs {
          title = "Projekt überprüft am ${dateTimeFormat.format(verification.verifiedAt, plannerI18nConfiguration)}"
        }
        faCircleCheck()
      }
    }

    if (currentConfiguration != null) {
      p("form-text") { +currentConfiguration.configurationName }
    }
    p("form-text") { +projectPreview.location.address.singleLine() }
  }

  fun <T : ServerProjectPreview> RDOMBuilder<*>.customerDetails(projectPreview: T) {
    +projectPreview.customer.completeName
    if (projectPreview.customer.address != projectPreview.location.address) {
      p("form-text") { +projectPreview.customer.address.singleLine() }
    }
    (projectPreview.customer.phone ?: projectPreview.customer.email)?.let { contactInfo ->
      p("form-text") { +contactInfo }
    }
  }

  fun <T : ServerProjectPreview> RDOMBuilder<*>.startupDateLine(projectPreview: T) {
    projectPreview.startupDate?.let { startupDate ->
      p("form-text mt-2") { +"Inbetriebnahme am ${dateFormat.format(startupDate, plannerI18nConfiguration)}" }
    }
  }

  val editProjectAction: (navigate: NavigateUrlFunction, projectPreview: ServerProjectPreview, projectQueryComponent: ProjectQueryComponent?) -> Unit = { navigate, projectPreview, projectQueryComponent ->
    if (projectQueryComponent == null) {
      projectPreview.mostAdvancedProcessStateEntry?.processState?.let { processState ->
        when (processState) {
          is AdvanceInvoiceProcessStates -> navigate(RouterUrls.project(projectPreview).advanceInvoice)
          is AssemblyBasementPreparationProcessStates, is AssemblyRoofPreparationProcessStates -> navigate(RouterUrls.project(projectPreview).prepareAssembly)
          is AssemblyBasementProcessStates -> navigate(RouterUrls.project(projectPreview).assemblyBasement)
          is AssemblyPortfolioProcessStates -> navigate(RouterUrls.project(projectPreview).assemblyPortfolio)
          is AssemblyRoofProcessStates -> navigate(RouterUrls.project(projectPreview).assemblyRoof)
          is BlueprintAcquisitionProcessStates, is BlueprintProcessStates -> navigate.toBlueprint(projectPreview)
          is ConfigurationProcessStates -> navigate(RouterUrls.project(projectPreview).configurations)
          is DocumentationProcessStates -> navigate(RouterUrls.project(projectPreview).documentation)
          is FinalAccountProcessStates -> navigate(RouterUrls.project(projectPreview).finalAccount)
          is FinishingUpProcessStates -> navigate(RouterUrls.project(projectPreview).finishingUp)
          is GridAssessmentProcessStates -> navigate(RouterUrls.project(projectPreview).gridAssessment)
          is OrderSpecialMaterialProcessStates -> navigate(RouterUrls.project(projectPreview).orderSpecialMaterial)
          is PresentationProcessStates, is QuoteOfferProcessStates -> navigate(RouterUrls.project(projectPreview).configurationUrl(projectPreview.currentConfigurationPreview?.configurationId))
          is QuoteConfirmationProcessStates -> navigate(RouterUrls.project(projectPreview).configurationUrl(projectPreview.currentConfigurationPreview?.configurationId))
          is ProjectProcessStates -> throw IllegalStateException("dataProcessStateEntry is not supposed to be ProjectProcessState")
          is StartupOperationsProcessStates -> navigate(RouterUrls.project(projectPreview).startupOperations)
          is SwitchMeterBoxProcessStates -> navigate(RouterUrls.project(projectPreview).switchMeterBox)
        }
      } ?: navigate.toProject(projectPreview)
    } else {
      when (projectQueryComponent) {
        ProjectQueryComponent.Project -> navigate.toProject(projectPreview)
        ProjectQueryComponent.Blueprint -> navigate.toBlueprint(projectPreview)
        ProjectQueryComponent.CurrentConfiguration -> navigate(RouterUrls.project(projectPreview).configurations)
        ProjectQueryComponent.CurrentQuote -> navigate(RouterUrls.project(projectPreview).configurationUrl(projectPreview.currentConfigurationPreview?.configurationId))
        ProjectQueryComponent.OrderSpecialMaterial -> navigate(RouterUrls.project(projectPreview).orderSpecialMaterial)
        ProjectQueryComponent.GridAssessment -> navigate(RouterUrls.project(projectPreview).gridAssessment)
        ProjectQueryComponent.AssemblyPortfolio -> navigate(RouterUrls.project(projectPreview).assemblyPortfolio)
        ProjectQueryComponent.ScheduleAssembly -> navigate(RouterUrls.project(projectPreview).scheduleAssembly)
        ProjectQueryComponent.AdvanceInvoice -> navigate(RouterUrls.project(projectPreview).advanceInvoice)
        ProjectQueryComponent.PrepareMaterial -> navigate(RouterUrls.project(projectPreview).prepareAssembly)
        ProjectQueryComponent.AssemblyRoof -> navigate(RouterUrls.project(projectPreview).assemblyRoof)
        ProjectQueryComponent.AssemblyBasement -> navigate(RouterUrls.project(projectPreview).assemblyBasement)
        ProjectQueryComponent.SwitchMeterBox -> navigate(RouterUrls.project(projectPreview).switchMeterBox)
        ProjectQueryComponent.StartupOperations -> navigate(RouterUrls.project(projectPreview).startupOperations)
        ProjectQueryComponent.FinishingUp -> navigate(RouterUrls.project(projectPreview).finishingUp)
        ProjectQueryComponent.FinalAccount -> navigate(RouterUrls.project(projectPreview).finalAccount)
        ProjectQueryComponent.Documentation -> navigate(RouterUrls.project(projectPreview).documentation)
        ProjectQueryComponent.Verification -> navigate(RouterUrls.project(projectPreview).documentation)
      }
    }
  }


}
